import React from 'react';
import { FaExclamationTriangle, FaEye, FaBan, FaCheckCircle } from 'react-icons/fa';

function FraudAlertCard({ fraud, onViewDetails, onBanUser, onResolve }) {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return '#F44336';
      case 'medium':
        return '#FF9800';
      case 'low':
        return '#FFC107';
      default:
        return '#999';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'high':
        return '🔴 YÜKSƏK';
      case 'medium':
        return '🟡 ORTA';
      case 'low':
        return '🟢 AŞAĞI';
      default:
        return 'N/A';
    }
  };

  const getFraudTypeLabel = (type) => {
    switch (type) {
      case 'multi_business_abuse':      return '🏪 Çox Kafeli Sui-istifadə';
      case 'rapid_checkins':            return '⚡ Sürətli Check-in';
      case 'impossible_location':       return '🗺️ Fiziki Qeyri-Mümkün Yerlər';
      case 'suspicious_tier_pattern':   return '💰 Şübhəli Xərcləmə Şablonu';
      case 'high_velocity_points':      return '🚀 Yüksək Xal Sürəti';
      case 'suspicious_business':       return '🏬 Şübhəli Kafe';
      case 'duplicate_account':         return '👥 Dublikat Hesab';
      case 'gps_spoofing':              return '📍 GPS Spoofing';
      case 'multiple_qr':               return '📱 Çoxlu QR Scan';
      case 'fake_account':              return '👤 Saxta Hesab';
      case 'spin_abuse':                return '🎰 Spin Sui-istifadəsi';
      default:                          return type;
    }
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return `${seconds} saniyə əvvəl`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} dəqiqə əvvəl`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} saat əvvəl`;
    return `${Math.floor(seconds / 86400)} gün əvvəl`;
  };

  return (
    <div
      style={{
        ...styles.card,
        borderLeft: `5px solid ${getSeverityColor(fraud.severity)}`,
      }}
    >
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <FaExclamationTriangle
            size={24}
            color={getSeverityColor(fraud.severity)}
          />
          <div>
            <h4 style={styles.fraudType}>{getFraudTypeLabel(fraud.fraud_type)}</h4>
            <span style={styles.severity}>{getSeverityLabel(fraud.severity)}</span>
          </div>
        </div>
        {fraud.resolved && (
          <span style={styles.resolvedBadge}>✅ Həll olundu</span>
        )}
      </div>

      {/* User Info */}
      <div style={styles.userInfo}>
        <p style={styles.label}>İstifadəçi:</p>
        <p style={styles.value}>
          {fraud.user_name || 'N/A'} (@{fraud.user_username || 'N/A'})
        </p>
      </div>

      {/* Business Info */}
      {fraud.business_name && (
        <div style={styles.info}>
          <p style={styles.label}>Business:</p>
          <p style={styles.value}>{fraud.business_name}</p>
        </div>
      )}

      {/* Details */}
      {fraud.details && (
        <div style={styles.details}>
          {fraud.details.description && (
            <p style={{ fontSize: 13, color: '#333', fontWeight: 600, marginBottom: 8 }}>
              {fraud.details.description}
            </p>
          )}
          <p style={styles.detailsText}>
            {JSON.stringify(
              Object.fromEntries(Object.entries(fraud.details).filter(([k]) => k !== 'description')),
              null, 2
            )}
          </p>
        </div>
      )}

      {/* Time */}
      <div style={styles.time}>
        <p style={styles.timeText}>{getTimeAgo(fraud.created_at)}</p>
      </div>

      {/* Actions */}
      {!fraud.resolved && (
        <div style={styles.actions}>
          <button onClick={() => onViewDetails(fraud)} style={styles.btnView}>
            <FaEye /> Ətraflı
          </button>
          <button onClick={() => onBanUser(fraud)} style={styles.btnBan}>
            <FaBan /> Ban Et
          </button>
          <button onClick={() => onResolve(fraud)} style={styles.btnResolve}>
            <FaCheckCircle /> Həll Et
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    marginBottom: '15px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
  },
  headerLeft: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
  },
  fraudType: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  severity: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  resolvedBadge: {
    padding: '5px 12px',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  userInfo: {
    marginBottom: '10px',
  },
  info: {
    marginBottom: '10px',
  },
  label: {
    fontSize: '12px',
    color: '#999',
    marginBottom: '3px',
  },
  value: {
    fontSize: '14px',
    color: '#1A1A2E',
    fontWeight: '500',
  },
  details: {
    backgroundColor: '#F8F9FA',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '10px',
  },
  detailsText: {
    fontSize: '12px',
    color: '#666',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    margin: 0,
  },
  time: {
    marginBottom: '15px',
  },
  timeText: {
    fontSize: '12px',
    color: '#999',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  btnView: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '8px 15px',
    backgroundColor: '#2196F3',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  btnBan: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '8px 15px',
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  btnResolve: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '8px 15px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
};

export default FraudAlertCard;